import { useEffect, useMemo, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, IbanElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, CheckCircle2, ShieldCheck, Landmark, Coins } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { isValidIban } from "@/lib/iban";

const eur = (n) => `€ ${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Traduce/normalizza i messaggi d'errore tecnici di Stripe in avvisi chiari in italiano.
const italianStripeError = (err) => {
  const msg = (err?.message || "").toLowerCase();
  const code = err?.code || "";
  if (msg.includes("chargeback") || msg.includes("blocked") || code === "payment_intent_payment_attempt_failed") {
    return "Pagamento bloccato da Stripe per rischio elevato di storno. Verifica che l'IBAN indicato sia corretto e intestato al condominio; se il problema persiste contatta l'assistenza.";
  }
  if (msg.includes("iban") || msg.includes("bank account") || code === "invalid_bank_account_iban") {
    return "IBAN non valido. Controlla di aver inserito correttamente l'IBAN del condominio.";
  }
  if (code === "processing_error" || msg.includes("try again")) {
    return "Si è verificato un errore temporaneo durante il pagamento. Riprova tra qualche istante.";
  }
  return err?.message || "Pagamento non riuscito. Verifica l'IBAN e riprova.";
};

const ibanStyleFor = (dark) => ({
  base: {
    fontSize: "15px",
    color: dark ? "#E5E7EB" : "#0A0A0A",
    "::placeholder": { color: dark ? "#6B7280" : "#9AA0A6" },
  },
  invalid: { color: dark ? "#fb7185" : "#e11d48" },
});

const SepaForm = ({ pending, options, onActivated, onClose, initialOption }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { effective } = useTheme();
  const isDark = effective === "dark";
  const [option, setOption] = useState(initialOption || "premium");
  const [name, setName] = useState(pending?.name || "");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  // Stato di validazione dell'IBAN digitato manualmente (fornito da Stripe IbanElement).
  const [ibanComplete, setIbanComplete] = useState(false);
  const [ibanError, setIbanError] = useState("");

  // IBAN già presente nell'anagrafica del condominio → riuso senza reinserirlo.
  const savedIban = (pending?.iban || "").replace(/\s+/g, "").toUpperCase();
  const hasSavedIban = savedIban.length > 0 && isValidIban(savedIban);
  const [useSaved, setUseSaved] = useState(hasSavedIban);
  useEffect(() => { setUseSaved(hasSavedIban); }, [hasSavedIban, pending?.id]);
  const fullIban = savedIban ? savedIban.replace(/(.{4})/g, "$1 ").trim() : "";

  useEffect(() => {
    api.get("/auth/me").then((r) => setEmail(r.data?.email || "")).catch(() => {});
  }, []);

  const selected = useMemo(() => options.find((o) => o.option === option) || {}, [options, option]);

  const submit = async () => {
    if (!stripe || !elements) return;
    if (!name.trim()) { toast.error("Inserisci l'intestatario del conto."); return; }
    if (!email.trim()) { toast.error("Inserisci un'email per il mandato SEPA."); return; }
    // Validazione IBAN prima di contattare Stripe (riduce i blocchi Radar da IBAN errati).
    if (useSaved && hasSavedIban) {
      if (!isValidIban(savedIban)) { toast.error("L'IBAN salvato non è valido. Inseriscine uno corretto."); return; }
    } else {
      if (ibanError) { toast.error(ibanError); return; }
      if (!ibanComplete) { toast.error("Inserisci un IBAN valido e completo prima di procedere."); return; }
    }
    setBusy(true);
    try {
      const payload = pending?._building_id
        ? { building_id: pending.id, option }
        : { pending_id: pending.id, option };
      const r = await api.post("/billing/condominio/subscription", payload);
      const { client_secret, subscription_id, billing_address } = r.data;
      const billingDetails = {
        name: name.trim(),
        email: email.trim(),
        ...(billing_address ? { address: {
          line1: billing_address.line1 || undefined,
          city: billing_address.city || undefined,
          postal_code: billing_address.postal_code || undefined,
          country: billing_address.country || "IT",
        } } : {}),
      };
      const paymentMethod = (useSaved && hasSavedIban)
        ? { sepa_debit: { iban: savedIban }, billing_details: billingDetails }
        : { sepa_debit: elements.getElement(IbanElement), billing_details: billingDetails };
      const result = await stripe.confirmSepaDebitPayment(client_secret, { payment_method: paymentMethod });
      if (result.error) {
        toast.error(italianStripeError(result.error));
        setBusy(false);
        return;
      }
      const st = result.paymentIntent?.status;
      if (st === "processing" || st === "succeeded" || st === "requires_action") {
        const act = await api.post("/billing/subscription/activate", { subscription_id });
        void act;
        setBusy(false);
        setSuccess(true);
        setTimeout(() => {
          toast.success("Collegamento attivato! Il condominio è ora nell'anagrafica.");
          onActivated();
        }, 1700);
      } else {
        toast.error("Stato del pagamento inatteso. Riprova.");
        setBusy(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile attivare il collegamento.");
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="sepa-form">
      {success && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm" data-testid="sepa-success">
          <div className="flex flex-col items-center justify-center text-center px-8 pop-in">
            <div className="relative mb-5 h-20 w-20 flex items-center justify-center">
              <span className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
              <span className="absolute inset-2 rounded-full bg-emerald-500/15" />
              <CheckCircle2 className="relative h-16 w-16 text-emerald-500 pop-in" strokeWidth={2.25} />
            </div>
            <div className="font-head text-xl font-extrabold">Collegamento attivato!</div>
            <div className="text-sm text-muted-foreground mt-1.5 max-w-xs">
              Il condominio «{pending?.name}» è ora nell'anagrafica. Ti abbiamo inviato l'email di conferma.
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {options.map((o) => {
          const active = option === o.option;
          const accent = o.option === "elite" ? "#C99700" : "#007BFF";
          return (
            <button key={o.option} type="button" data-testid={`sepa-option-${o.option}`}
              onClick={() => setOption(o.option)}
              style={{ borderColor: active ? accent : undefined, backgroundColor: active ? `${accent}0F` : undefined }}
              className={`relative text-left rounded-xl border-2 p-4 transition-all duration-200 ${active ? "shadow-sm" : "border-border hover:border-foreground/20"}`}>
              {active && <CheckCircle2 className="absolute top-3 right-3 h-5 w-5" style={{ color: accent }} />}
              {o.sedio_coin > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full mb-1.5"
                  style={{ backgroundColor: `${accent}1A`, color: accent }}>
                  <Coins className="h-3 w-3" />+{o.sedio_coin} SedioCoin
                </span>
              )}
              <div className="font-head text-sm font-bold" style={{ color: accent }}>{o.label}</div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="font-head text-2xl font-extrabold tracking-tight">{eur(o.net_eur)}</span>
                <span className="text-[11px] text-muted-foreground">/anno + IVA</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">≈ {eur(o.est_gross_eur)} IVA inclusa</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Intestatario conto *</label>
          <Input data-testid="sepa-holder" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome intestatario" className="h-9 mt-1" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Email *</label>
          <Input data-testid="sepa-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@condominio.it" className="h-9 mt-1" />
        </div>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 mb-1">
          <Landmark className="h-3 w-3" /> IBAN del condominio *
        </label>
        {hasSavedIban && useSaved ? (
          <div className="flex items-center justify-between gap-3 border border-input rounded-md px-3 py-2.5 bg-background" data-testid="sepa-iban-saved">
            <div className="min-w-0">
              <div className="font-mono text-sm break-all">{fullIban}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">IBAN dall'anagrafica del condominio</div>
            </div>
            <button type="button" data-testid="sepa-iban-use-other" onClick={() => setUseSaved(false)}
              className="text-[12px] font-medium text-[#007BFF] hover:underline shrink-0">
              Usa un altro IBAN
            </button>
          </div>
        ) : (
          <>
            <div className="border border-input rounded-md px-3 py-2.5 bg-background" data-testid="sepa-iban-element">
              <IbanElement key={isDark ? "dark" : "light"}
                options={{ supportedCountries: ["SEPA"], placeholderCountry: "IT", style: ibanStyleFor(isDark) }}
                onChange={(e) => {
                  setIbanComplete(e.complete);
                  setIbanError(e.error ? "IBAN non valido: controlla di averlo inserito correttamente." : "");
                }}
              />
            </div>
            {ibanError && (
              <p className="mt-1.5 text-[12px] text-rose-600 dark:text-rose-400" data-testid="sepa-iban-error">{ibanError}</p>
            )}
            {hasSavedIban && (
              <button type="button" data-testid="sepa-iban-use-saved" onClick={() => setUseSaved(true)}
                className="mt-1.5 text-[12px] font-medium text-[#007BFF] hover:underline">
                Usa l'IBAN salvato ({fullIban})
              </button>
            )}
          </>
        )}
      </div>

      <div className="text-[11px] text-muted-foreground bg-secondary/50 rounded-md p-3 leading-relaxed" data-testid="sepa-mandate">
        Autorizzando questo pagamento, consenti a Sedio e a Stripe, nostro fornitore di servizi di pagamento,
        di inviare istruzioni alla tua banca per addebitare il tuo conto e alla tua banca di addebitare il conto
        secondo tali istruzioni, tramite addebito diretto SEPA. Il collegamento si rinnova automaticamente ogni anno.
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-md">Annulla</Button>
        <Button data-testid="sepa-activate-button" onClick={submit} disabled={busy || !stripe || (useSaved && hasSavedIban ? false : !ibanComplete)}
          className="bg-[#007BFF] hover:bg-[#0069D9] rounded-md">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
          Attiva collegamento {selected.net_eur != null ? `— ${eur(selected.net_eur)}/anno + IVA` : ""}
        </Button>
      </div>
    </div>
  );
};

export const SepaCheckoutDialog = ({ pending, options, onClose, onActivated, initialOption }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const open = !!pending;

  useEffect(() => {
    if (!open || stripePromise) return;
    api.get("/billing/config").then((r) => {
      const pk = r.data?.publishable_key;
      if (pk) setStripePromise(loadStripe(pk));
      else toast.error("Configurazione pagamenti non disponibile.");
    }).catch(() => toast.error("Configurazione pagamenti non disponibile."));
  }, [open, stripePromise]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sepa-dialog" className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Collega "{pending?.name}"</DialogTitle>
          <DialogDescription>
            Scegli l'opzione e completa l'addebito diretto SEPA. Il condominio si attiva subito dopo l'accettazione del mandato.
          </DialogDescription>
        </DialogHeader>
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <SepaForm pending={pending} options={options} onClose={onClose} onActivated={onActivated} initialOption={initialOption} />
          </Elements>
        ) : (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        )}
      </DialogContent>
    </Dialog>
  );
};
